import type { Auth, User } from "firebase/auth";
import { useCallback, useState } from "react";
import * as api from "./api";
import { galText, STATUS_TEXT } from "./accounts";
import { go, usePoll } from "./hooks";
import { signOut } from "./signOut";
import {
  bestText,
  canResize,
  forcedSwap,
  MINIMUM_MARK,
  minutesText,
  overflowsText,
  paybackText,
  SIZE_COLUMNS,
  type SizeRow,
  sizeText,
} from "./sizes";
import type { Sort } from "./sort";
import { SortTh } from "./SortHeader";
import { resizeLink } from "./workOrders";

type SizeFail = "network" | "server" | "session" | null;

/** The one read of the Sizes tab and the Size savings page. */
export function useSizeAccounts(user: User, canView: boolean | undefined) {
  const [accounts, setAccounts] = useState<api.Account[] | undefined>(undefined);
  const [prices, setPrices] = useState<api.SavingPrices | undefined>(undefined);
  const [failed, setFailed] = useState<SizeFail>(null);
  const fetchAccounts = useCallback(
    async (signal: AbortSignal) => {
      try {
        const found = await api.sizeAccounts(await user.getIdToken(), signal);
        if (signal.aborted) return;
        setAccounts(found.accounts);
        setPrices(found.saving_prices);
        setFailed(null);
      } catch (e) {
        if (signal.aborted) return;
        const status = e instanceof api.ApiError ? e.status : 0;
        setFailed(status === 401 || status === 403 ? "session" : status >= 500 ? "server" : "network");
      }
    },
    [user],
  );
  const load = usePoll(fetchAccounts, canView === true);
  return { accounts, prices, failed, load };
}

export function SizeFailure({ failed, auth, onRetry }: { failed: SizeFail; auth: Auth; onRetry: () => void }) {
  if (failed === "session") {
    return (
      <p role="alert" className="error">
        Your session ended.{" "}
        <button className="primary" onClick={() => signOut(auth)}>
          Sign in again
        </button>
      </p>
    );
  }
  if (!failed) return null;
  return (
    <p role="alert" className="error">
      {failed === "server" ? "The server has a problem. Try again in a few minutes." : "Cannot reach the server."} <button onClick={onRetry}>Try again</button>
    </p>
  );
}

/** The table of the sizes and the savings. The best size opens the resize work order of R-3. */
export function SizeTable({ rows, base, sort, onSort }: { rows: readonly SizeRow[]; base: string; sort: Sort | null; onSort: (sort: Sort | null) => void }) {
  return (
    <div className="table-scroll" tabIndex={0} role="region" aria-label="Container sizes">
      <table className="list" data-testid="sizes-list" data-cols="sizes-sort">
        <thead>
          <tr>
            {SIZE_COLUMNS.map((column) => (
              <SortTh key={column.key} column={column} sort={sort} onSort={onSort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const { account, recommended, saving } = row;
            const forced = forcedSwap(row);
            return (
              <tr key={account.id} data-testid="sizes-row" data-account={account.number}>
                <th scope="row" className="account-number">
                  <a href={`${base}/accounts/${account.id}`} onClick={go}>
                    {account.number}
                  </a>
                </th>
                <td data-label="">
                  <span className="clip name" title={account.name}>
                    {account.name}
                  </span>
                </td>
                <td data-label="Area">{account.plan?.area ?? "–"}</td>
                <td className="num" data-label="Container size">
                  {sizeText(account.capacity_gal)}
                </td>
                <td className="num" data-label="Oil each cycle">
                  {account.projection?.need_gal != null ? galText(account.projection.need_gal) : "–"}
                </td>
                <td className="num" data-label="Recommended" data-testid="account-recommended">
                  {recommended ? <span className={recommended.strong ? "recommend-change" : undefined}>{recommended.text}</span> : "No estimate"}
                </td>
                <td className="num" data-label="Best size" data-testid="sizes-best">
                  {saving === null ? (
                    "–"
                  ) : canResize(saving) ? (
                    <a href={resizeLink(base, account.id, saving.best_gal)} onClick={go} data-testid="sizes-resize" title="Open the resize work order">
                      {galText(saving.best_gal)}
                    </a>
                  ) : (
                    bestText(saving, account.projection, recommended?.text)
                  )}
                </td>
                <td className="num" data-label="Minutes saved a month" data-testid="sizes-minutes">
                  {saving ? minutesText(saving.minutes_month) : "–"}
                </td>
                <td className="num" data-label="Payback" data-testid="sizes-payback">
                  {forced && (
                    <abbr className="size-min" title="Minimum size" data-testid="sizes-min">
                      {MINIMUM_MARK}
                    </abbr>
                  )}
                  {forced && saving?.payback_weeks === null ? null : paybackText(saving?.payback_weeks ?? null)}
                </td>
                <td className="num" data-label="Overflows avoided a month" data-testid="sizes-overflows">
                  {saving ? overflowsText(saving.overflows_month) : "–"}
                </td>
                <td data-label="Status">{STATUS_TEXT[account.status]}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
